import {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import AdminBanner from './AdminBanner';
import useLoadBannerSdk from '../../../commonSub/hook/useLoadBannerSdk';
import useIntersection from '../../hook/useIntersection';
import useTypedSelector from '../../hook/useTypedSelector';
import {IAdminBannerComponent} from '../../type';
import Loading from '../Loading';

interface IProps {
  unitId: string;
  w: number;
  h: number;
  defaultBanner?: IAdminBannerComponent;
  bannerClassName?: string;
  onLoadMoment?: () => void;
}

export default function MomentBanner({
  unitId,
  w,
  h,
  defaultBanner,
  bannerClassName,
  onLoadMoment,
}: IProps) {
  const dispatch = useDispatch();
  useLoadBannerSdk('moment');
  const isLoaded = useTypedSelector(
    state => state.common.isLoadedBannerSdk.moment
  );
  const [showDefaultBanner, setShowDefaultBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const ref = useRef<HTMLModElement>(null);
  const observerRef = useRef<MutationObserver>();
  useEffect(() => {
    if (!observerRef.current && ref.current) {
      observerRef.current = new MutationObserver(mutations => {
        for (const {target} of mutations) {
          const classList = (target as HTMLElement).classList;
          const isHideMoment = classList.contains(IS_HIDE_MOMENT);
          const isLoadMoment = classList.contains(IS_LOAD_MOMENT);
          if (isHideMoment || isLoadMoment) {
            setIsLoading(false);
            setShowDefaultBanner(isHideMoment);
            observerRef.current?.disconnect();
            observerRef.current = undefined;
            break;
          }
        }
      });
      observerRef.current.observe(ref.current, {
        attributeFilter: ['class'],
      });
    }
    (window as any).onAdFail ??= (el: HTMLElement) => {
      // NO_AD 또는 통신 오류일때 실행된다(테스트 해봄).
      const id = el.dataset.adUnit;
      el.classList.add(IS_HIDE_MOMENT, IS_LOAD_MOMENT);
      (window as any).adfit.destroy(id);
    };
    (window as any).onAdLoad ??= (el: HTMLElement) => {
      el.classList.add(IS_LOAD_MOMENT);
    };
  }, []);

  useEffect(() => {
    !isLoading && onLoadMoment?.();
  }, [isLoading, onLoadMoment]);

  const divRef = useRef<HTMLDivElement>(null);
  const [isIntersection, setIsIntersection] = useState(false);
  useIntersection(
    {
      targetRef: divRef,
      onIntersection: setIsIntersection,
    },
    !isLoaded || isIntersection
  );

  useEffect(() => {
    if (!ref.current || !isLoaded || !isIntersection) return;
    // http://guide.ad.sdk.at.kakaocorp.com/web/banner-ad/4_api.html
    const adfit = (window as any).adfit;
    adfit?.render(ref.current);
    return () => {
      adfit?.destroy(unitId);
      observerRef.current?.disconnect();
    };
  }, [unitId, dispatch, isLoaded, isIntersection]);

  return (
    <div ref={divRef} className="relative">
      {isLoading && (
        <div className="absolute left-1/2 top-1/2 -ml-10 -mt-10">
          <Loading />
        </div>
      )}
      {showDefaultBanner && defaultBanner && (
        <AdminBanner type="adMoment" adminAd={defaultBanner} />
      )}
      {!showDefaultBanner && (
        <ins
          ref={ref}
          className={cn('kakao_ad_area', bannerClassName)}
          style={{width: '100%', height: `${h}px`, display: 'block'}}
          data-ad-unit={unitId}
          data-ad-width={w}
          data-ad-height={h}
          data-ad-onfail="onAdFail"
          data-ad-onload="onAdLoad"
          data-ad-landing-target="_blank"
        />
      )}
    </div>
  );
}

const IS_HIDE_MOMENT = 'hide-moment';
const IS_LOAD_MOMENT = 'load-moment';
